import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FieldResult} from '../model/api/field-result';
import {FieldRecord} from '../model/api/field-record';
import {SessionNames} from "../shared/session/session-names";
import {CCRolesType} from "../model/security/cc-roles-type";
import {DateTime} from "luxon";
import {Subject} from "rxjs";


@Directive()
export abstract class CcBaseComponent<T = any> implements OnInit, OnChanges, AfterViewInit, AfterContentInit, OnDestroy {

  protected readonly destroy$ = new Subject<void>();

  pageUrl: string = window.location.href;
  userRoles: CCRolesType.Roles[] = [];

  destroyed: boolean = false;
  dateCreation: DateTime = DateTime.now();

  constructor(protected cdref: ChangeDetectorRef,
              protected messageService: MessageService,
              protected translateService: TranslateService) {
    this.userRoles = JSON.parse(sessionStorage.getItem(SessionNames.USER_ROLES));
  }

  ngAfterContentInit(): void {
  }

  public markForCheck() {
    if (this.cdref) {
      this.cdref.markForCheck();
    }
  }

  ngOnInit(): void {
    // setTimeout(() => {
    // queueMicrotask(() => {
    // this.initConfigs();

    // this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
    queueMicrotask(() => {
      this.initConfigs();
      this.initData();
      this.postInitConfigs();
    });
    // });
    // });
    // setTimeout(() => {
    // queueMicrotask(() => this.initData());
    // this.initData();
    // })
    // queueMicrotask(() => this.initData());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // this.detectChanges();
  }

  ngAfterViewInit(): void {
    // this.postInitConfigs();
  }

  protected initConfigs() {
  }


  protected postInitConfigs() {
  }

  protected initData() {
  }

  public warnMessage(message: string) {
    if (message) {
      console.log('Warning: ' + message);
      this.messageService.add({
        severity: 'warn',
        icon: 'fas fa-triangle-exclamation',
        summary: this.getTranslation('common.message.warn'),
        detail: message,
        life: 10000
      });
    }
  }

  public errorMessage(message: string) {
    if (message) {
      console.log('Error: ' + message);
      this.messageService.add({
        severity: 'error',
        icon: 'fas fa-bomb',
        summary: this.getTranslation('common.message.error'),
        detail: message,
        // Errors must stay until the user dismisses them: they often carry a
        // support reference code that has to be read/copied (see copy button in the
        // toast template). A timed life made them vanish before that was possible.
        sticky: true,
        closable: true
      });
    }
  }

  /**
   * Routes a backend ApiValidation/TableRecordResult message to the right toast.
   * Failures (success === false) — including the generic "reference code" errors
   * the backend returns with HTTP 200 — always show as an error toast, so the
   * severity no longer depends on which component happens to handle the response.
   */
  public apiMessage(result: { success?: boolean, message?: string } | undefined | null) {
    if (!result || !result.message || result.message === 'success') {
      return;
    }
    if (result.success === false) {
      this.errorMessage(result.message);
    } else {
      this.infoMessage(result.message);
    }
  }

  protected successMessage(message: string) {
    if (message)
      this.messageService.add({
        severity: 'success',
        icon: 'fas fa-thumbs-up',
        // summary: this.getTranslation('common.message.successful'),
        detail: message,
        life: 3000
      });
  }

  protected infoMessage(message: string) {
    if (message)
      this.messageService.add({
        severity: 'info',
        icon: 'fas fa-info-circle',
        // summary: this.getTranslation('common.message.info'),
        detail: message,
        life: 10000
      });
  }

  protected getFields(fieldResult: FieldResult<any>): FieldRecord<any>[] {
    if (fieldResult.success) {
      return fieldResult.records ? fieldResult.records : [];
    } else if (fieldResult.message) {
      this.messageService.add({
        severity: 'error',
        summary: this.getTranslation('common.message.error'),
        detail: fieldResult.message
      });
      return [];
    }
    return [];
  }

  findOptText(options: FieldRecord<any>[], id: string): string {
    // console.log('-----> findOptText');
    if (options) {
      let foundField = options.find(Option => (!Option.id && (!id || id.length === 0)) || Option.id === id + ''
        || (Option.text && Option.text === id));
      // console.log("----> foundField  " + field + " " + id + " " + JSON.stringify(foundField));
      if (foundField) {
        return foundField.text;
      }
    }
    return '';
  }

  getDistinctOpts(options: FieldRecord<any>[]): FieldRecord<any>[] {
    if (options) {
      const addedTexts = [];
      return options.filter(option => {
        if (addedTexts.includes(option.text)) false;
        else {
          addedTexts.push(option.text);
          return true;
        }
      });
    }
    return options;
  }

  protected getTranslation(key: string, params: any = undefined) {
    return key && key.length > 0 ? this.translateService.instant(key, params) : "";
  }

  public extractReferenceCode(text: string): string | null {
    if (!text) {
      return null;
    }
    const match = /reference code:\s*([A-Za-z0-9]+)/i.exec(text);
    return match ? match[1] : null;
  }

  /**
   * Reference code for a toast message. The code can live in either the summary
   * (interceptor path) or the detail (errorMessage path), so both are checked.
   */
  public toastReferenceCode(message: any): string | null {
    if (!message) {
      return null;
    }
    return this.extractReferenceCode(message.detail) || this.extractReferenceCode(message.summary);
  }

  public copiedToClipboard() {
    this.messageService.add({
      severity: 'success',
      icon: 'fas fa-clipboard-check',
      detail: "Copied to Clipboard!",
      life: 1000
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected isAdmin(): boolean {
    return this.hasRoles([CCRolesType.Roles.ROLE_SYSTEM]);
  }

  hasRoles(rolesFilter: CCRolesType.Roles[]): boolean {
    let hasRoles: boolean = false;
    if (this.userRoles) {
      this.userRoles.forEach(value => {
        if (rolesFilter.some(rolesFilter => rolesFilter == value)) {
          hasRoles = true;
        }
      });
    }
    return hasRoles;
  }
}
